import axios from 'axios';
import { handleAuthError } from '../funciones/authHelper';
import { getCachedData, CACHE_KEYS } from '../funciones/apiCache';

const API_URL = process.env.REACT_APP_API_URL;

const CUMPLEANOS_CACHE_TTL = 60 * 60 * 1000;

export const fetchPersonasData = async ({ excluirColaboradores = false, incluirBajas = false } = {}) => {
    try {
        const params = {};
        if (excluirColaboradores) params.excluirColaboradores = 'true';
        /* APAGADO POR DEFECTO, y por eso el parametro es opcional: las otras seis pantallas que
         * llaman aqui no lo pasan y siguen recibiendo lo de siempre.
         *
         * Se llama BAJAS y no «inactivos» porque eso es lo que añade: `estado = Baja`. Lo medimos
         * tarde —siete de los ocho `activo = 0` eran «En Proceso», gente que todavia NO HA
         * LLEGADO—, y el nombre viejo describia el eje equivocado.
         *
         * El servidor lo exige detras de `vacaciones.bajas.ver` y RECHAZA con 403 si se pide
         * sin tenerlo; no lo ignora. Asi que pedirlo sin permiso se nota. */
        if (incluirBajas) params.incluir_bajas = '1';
        const response = await axios.get(`${API_URL}/api/personas`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem("token")}` },
            params
        });
        return response.data;
    } catch (error) {
        handleAuthError(error);
    }
};

export const fetchPersonaData = async (id) => {
    try {
        const response = await axios.get(`${API_URL}/api/persona/${id}`, {headers: {
            'Authorization': `Bearer ${localStorage.getItem("token")}`
        }});
        return response.data;
    } catch (error) {
        handleAuthError(error);
    }
};

/**
 * Fase A1 · la puerta pasa de la contraseña propia al SEGUNDO FACTOR.
 *
 * No se suma: SUSTITUYE. Un código es más fuerte que la propia contraseña, que es justo lo que
 * puede estar comprometido. `ventana` es la autorización corta que devuelve
 * `/api/seguridad/verificar-2fa`.
 */
export const fetchDatosSoporte = async (id, ventana, tipo = null) => {
    const query = tipo ? `?tipo=${tipo}` : '';
    const response = await axios.post(`${API_URL}/api/persona/${id}/soporte${query}`, { ventana }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem("token")}` }
    });
    return response.data;
};

export const fetchRolesData = async () => {
    try {
        const response = await axios.get(`${API_URL}/api/roles`, {headers: { 
            'Authorization': `Bearer ${localStorage.getItem("token")}`
        }});
        return response.data;
    } catch (error) {
        handleAuthError(error);
    }
};

/**
 * Los tipos de contrato del catalogo cerrado. La pantalla NO los lleva escritos: se piden, igual
 * que los roles del sistema, para que ampliar la lista no obligue a tocar el frontend.
 * Devuelve `[{ clave, etiqueta }]` — la clave se guarda, la etiqueta se enseña.
 */
export const fetchTiposContrato = async () => {
    try {
        const response = await axios.get(`${API_URL}/api/tipos-contrato`, { headers: {
            'Authorization': `Bearer ${localStorage.getItem("token")}`,
        }});
        return response.data?.tipos || [];
    } catch (error) {
        handleAuthError(error);
        return [];
    }
};

export const fetchEstadosData = async () => {
    try {
        const response = await axios.get(`${API_URL}/api/estados`, {headers: { 
            'Authorization': `Bearer ${localStorage.getItem("token")}`
        }});
        return response.data;
    } catch (error) {
        handleAuthError(error);
    }
};

export const fetchDepartamentosData = async () => {
    try {
        const response = await axios.get(`${API_URL}/api/departamentos`, {headers: {
            'Authorization': `Bearer ${localStorage.getItem("token")}`
        }});
        return response.data;
    } catch (error) {
        handleAuthError(error);
    }
};

export const fetchAdjuntosData = async (idEmpresa, idDepartamento) => {
    try {
        const response = await axios.get(`${API_URL}/api/personas/empresa/${idEmpresa}/departamento/${idDepartamento}`, {headers: {
            'Authorization': `Bearer ${localStorage.getItem("token")}`
        }});
        return response.data;
    } catch (error) {
        handleAuthError(error);
    }
};

export const fetchNegociosData = async () => {
    try {
        const response = await axios.get(`${API_URL}/api/negocios`, {headers: { 
            'Authorization': `Bearer ${localStorage.getItem("token")}`
        }});
        return response.data;
    } catch (error) {
        handleAuthError(error);
    }
};

/* EL MISMO SUELO QUE EN LA GEMELA DE COLABORADORAS, y por la misma razon: reflejaba el
 * `whereCondition = { activo: true }` que el servidor tenia antes de la Fase D. Aqui NO llego a
 * morder porque su unico llamante pasa `null` explicito — pero eso es suerte, no diseño: el dia
 * que alguien la llame con diez argumentos vuelve a filtrar, y el nombre no avisa.
 * Se iguala a `null`: no filtrar por `activo` salvo que alguien lo pida. */
export const fetchPersonasFiltersAdminData = async (paginaActual, registrosPorPag, rol, estado, departamento, negocio, busqueda, ordenarColumna, orden, mostrarDependientes = false, activo = null, colaboradores = false, segundoFactor = null, ventana = null, empresaContratante = null) => {
    try {
        const response = await axios.post(`${API_URL}/api/personas/adminfilters`, {

            "current_page": paginaActual,
            "items_per_page": registrosPorPag,
            "filteredWord": busqueda,
            "rol": rol,
            "estado": estado,
            "departamento": departamento,
            "negocio": negocio,
            "activo": activo,
            "mostrar_dependientes": mostrarDependientes,
            "colaboradores": colaboradores,
            "segundo_factor": segundoFactor,   // Fase A1 · null = sin filtrar
            "ventana": ventana,                // Fase A1 · sin ella, el listado NO trae secretos
            // null = sin filtrar. El recuento de las que faltan viene SIEMPRE, filtre o no:
            // es lo que convierte el listado en la herramienta con la que se rellenan las 270.
            "empresa_contratante": empresaContratante,
            // true = solo las fichas donde `activo` y la situacion se contradicen.
            "order": {
                "orderColumn": ordenarColumna || "",
                "order": orden || ""
            }

        }, {headers: {
            'Authorization': `Bearer ${localStorage.getItem("token")}`
        }});

        return response.data;
    } catch (error) {
        handleAuthError(error);
    }
};

export const fetchPersonasFiltersData = async (paginaActual, registrosPorPag, rol, estado, departamento, negocio, busqueda, ordenarColumna, orden, mostrarDependientes = false, activo = null, colaboradores = false, segundoFactor = null, ventana = null, empresaContratante = null) => {
    try {
        const response = await axios.post(`${API_URL}/api/personas/filters`, {

            "current_page": paginaActual,
            "items_per_page": registrosPorPag,
            "filteredWord": busqueda,
            "rol": rol,
            "estado": estado,
            "departamento": departamento,
            "negocio": negocio,
            "activo": activo,
            "mostrar_dependientes": mostrarDependientes,
            "colaboradores": colaboradores,
            "segundo_factor": segundoFactor,   // Fase A1 · null = sin filtrar
            "ventana": ventana,                // Fase A1 · sin ella, el listado NO trae secretos
            // null = sin filtrar. El recuento de las que faltan viene SIEMPRE, filtre o no:
            // es lo que convierte el listado en la herramienta con la que se rellenan las 270.
            "empresa_contratante": empresaContratante,
            // true = solo las fichas donde `activo` y la situacion se contradicen.
            "order": {
                "orderColumn": ordenarColumna || "",
                "order": orden || ""
            }

        }, {headers: {
            'Authorization': `Bearer ${localStorage.getItem("token")}`
        }});

        return response.data;
    } catch (error) {
        handleAuthError(error);
    }
};

export const fetchCrearPersona = async (formData) => {
    console.log("formData_feeeee", formData);
    try {

        // Si hay una imagen nueva, convertirla a base64 y remplazar el objeto File con el string base64
        if (formData.imagenPersona_img) {
            const file = formData.imagenPersona_img;
            const base64Image = await convertToBase64(file);

            formData.imagen_base64 = base64Image;
            delete formData.imagenPersona_img;
        }

        const response = await axios.post(`${API_URL}/api/persona`, formData, {

            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem("token")}`
                /*"Access-Control-Allow-Origin": "*"*/
            }

        });
        
        return response;
    } catch (error) {
        handleAuthError(error);
    }
};

export const fetchEditarPersona = async (personaData) => {
    try {
        // Si hay una imagen nueva, convertirla a base64 y remplazar el objeto File con el string base64
        if (personaData.imagenPersona_img) {
            const file = personaData.imagenPersona_img;
            const base64Image = await convertToBase64(file);
            
            personaData.imagen_base64 = base64Image;
            delete personaData.imagenPersona_img;
        }
        
        const response = await axios.put(`${API_URL}/api/editarPersona/${personaData.Id}`, personaData, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem("token")}`
                /*"Access-Control-Allow-Origin": "*"*/
            }
        });

        return response;
    } catch (error) {
        handleAuthError(error);
    }
};

const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });
};

export const fetchEliminarPersona = async (ids) => {
    try {
        //console.log(ids);
        const response = await axios.post(`${API_URL}/api/persona/eliminar`, {
            ids: ids
        }, {headers: { 
            'Authorization': `Bearer ${localStorage.getItem("token")}`
        }});

        return response.data;
    } catch (error) {
        handleAuthError(error);
    }
};

/**
 * Suspender o levantar en grupo.
 *
 * Era `fetchActivoPersona`, que mandaba `{ids, activo}` a una ruta que escribia `activo` a pelo.
 * Ahora la situacion y el acceso se escriben JUNTOS, por el servicio que sabe la regla.
 *
 * Devuelve `aplicadas` y `omitidas`: en una seleccion de veinte habra gente en Baja Medica o ya
 * suspendida a la que no se le aplica, y contestar «hecho» seria mentir sobre esas.
 */
export const fetchSuspensionPersonas = async (ids, suspender) => {
    try {
        const response = await axios.post(`${API_URL}/api/personas/suspension`,
            { ids, suspender },
            { headers: { 'Authorization': `Bearer ${localStorage.getItem("token")}` } });
        return response.data;
    } catch (error) {
        handleAuthError(error);
        throw error;
    }
};

export const fetchProximosCumpleanos = async (limite = 3) => {
    const cacheKey = `${CACHE_KEYS.PROXIMOS_CUMPLEANOS}_${limite}`;

    const fetchFn = async () => {
        const response = await axios.get(`${API_URL}/api/personas/proximos-cumpleanos?limite=${limite}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem("token")}`
            }
        });
        return response.data;
    };

    try {
        return await getCachedData(cacheKey, fetchFn, CUMPLEANOS_CACHE_TTL);
    } catch (error) {
        handleAuthError(error);
    }
};